"use client";

import { appConfig } from "@/constants";
import { usePassportProvider } from "@/context";
import { useAccessToken } from "@/hooks/useAccessToken";
import type { ChainAddress } from "@/types";
import type { Chain } from "@/types";
import { EnvironmentNames } from "@/types";
import { authorizedGet } from "@/utils/request";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Address } from "viem";
import { immutableZkEvm, immutableZkEvmTestnet } from "viem/chains";
import { getMainnetBalances } from "./mainnet-eject/getMainnetBalances";
import { getZkEvmBalances } from "./mainnet-eject/getZkEvmBalances";
import type { MainnetBalance, MinimalCoinBalance } from "./types";
import { addZeroBalanceCoins } from "./utils";

/**
 * Generic hook for fetching chain balances.
 * Supports zkEVM, Immutable X, and Ethereum chains.
 */
export function useChainBalances(
  chain: Chain,
  walletAddress: ChainAddress | undefined,
): {
  data: MinimalCoinBalance[] | undefined;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  error: any;
  isLoading: boolean;
  // For Ethereum chain, also expose raw MainnetBalance[] and refetch
  rawData?: MainnetBalance[];
  refetch?: () => void;
} {
  // zkEVM balances via Passport profile API
  const zkEvmQuery = useZkEvmBalances(walletAddress);

  // Immutable X balances via Passport profile API
  const imtblXQuery = useImtblXBalances(walletAddress);

  // Ethereum balances
  const ethQuery = useEthereumBalances(walletAddress, chain === "eth");

  // Return the appropriate query based on chain
  if (chain === "imtbl-zkevm") {
    return zkEvmQuery;
  }
  if (chain === "imtbl-x") {
    return imtblXQuery;
  }
  if (chain === "eth") {
    // Ethereum balances need to be transformed from MainnetBalance to MinimalCoinBalance
    return {
      data: ethQuery.data
        ? ethQuery.data.map((balance) => ({
            name: balance.tokenName,
            symbol: balance.symbol,
            contractAddress:
              balance.address === "ETH"
                ? "native"
                : (balance.address as `0x${string}`),
            balance: balance.balance,
            imageUrl: balance.tokenImage,
            decimals: balance.decimals,
          }))
        : undefined,
      error: ethQuery.error,
      isLoading: ethQuery.isLoading,
      rawData: ethQuery.data,
      refetch: () => {
        void ethQuery.refetch();
      },
    };
  }

  return {
    data: undefined,
    error: null,
    isLoading: false,
  };
}

/**
 * Internal hook for zkEVM balances.
 * Uses viem multicall to batch query token balances directly from the chain.
 * The Passport profile API endpoint `/passport-profile/v1/networks/zkEvm/coins`
 * returns "unsupported network" error, so we query on-chain instead.
 */
function useZkEvmBalances(walletAddress: ChainAddress | undefined): {
  data: MinimalCoinBalance[] | undefined;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  error: any;
  isLoading: boolean;
} {
  const { zkEvmProvider } = usePassportProvider();

  // Determine chainId from environment
  const chainId = useMemo(() => {
    if (appConfig.ENVIRONMENT === EnvironmentNames.PRODUCTION) {
      return immutableZkEvm.id; // 13371
    }
    return immutableZkEvmTestnet.id; // 13473 (testnet/devnet)
  }, []);

  return useQuery<MinimalCoinBalance[]>({
    queryKey: ["chain-balances", "imtbl-zkevm", walletAddress, chainId],
    enabled: !!walletAddress && !!zkEvmProvider,
    queryFn: async () => {
      if (!walletAddress || !zkEvmProvider) return [];
      try {
        const balances = await getZkEvmBalances(
          zkEvmProvider,
          walletAddress as Address,
          chainId,
        );
        // Add zero balance coins (e.g., IMX if balance is 0)
        return addZeroBalanceCoins(chainId, balances);
      } catch (error) {
        console.error("Failed to fetch zkEVM balances:", error);
        // Return zero balance coins on error so UI still shows IMX
        return addZeroBalanceCoins(chainId, []);
      }
    },
  });
}

/**
 * Internal hook for Immutable X balances using Passport profile API
 */
function useImtblXBalances(walletAddress: ChainAddress | undefined): {
  data: MinimalCoinBalance[] | undefined;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  error: any;
  isLoading: boolean;
} {
  const getAccessToken = useAccessToken();

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const query = useQuery<any, any, MinimalCoinBalance[], any>({
    queryKey: ["chain-balances", "imtbl-x", walletAddress],
    enabled: !!walletAddress,
    queryFn: async () => {
      const accessToken = await getAccessToken();
      if (!accessToken || !walletAddress) return [];
      try {
        const data = await authorizedGet(
          "/passport-profile/v1/networks/starkEx/coins",
          accessToken,
        );
        // Ensure we return an array
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Failed to fetch Immutable X balances:", error);
        return [];
      }
    },
    select: (data) => {
      // Handle case where data might not be an array
      if (!data || !Array.isArray(data)) {
        return [];
      }
      return data.map(
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        (coinBalance: any) =>
          <MinimalCoinBalance>{
            name: coinBalance.name,
            symbol: coinBalance.symbol,
            contractAddress: coinBalance.contract_address,
            balance: coinBalance.balance,
            imageUrl: coinBalance.image_url,
            decimals: coinBalance.decimals,
          },
      );
    },
  });

  return query;
}

/**
 * Internal hook for Ethereum balances using Alchemy
 */
function useEthereumBalances(
  walletAddress: ChainAddress | undefined,
  enabled: boolean,
) {
  return useQuery<MainnetBalance[]>({
    queryKey: ["chain-balances", "eth", walletAddress],
    queryFn: async () => {
      if (!walletAddress) {
        return [];
      }
      return getMainnetBalances(walletAddress);
    },
    enabled: enabled && !!walletAddress,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
  });
}
